import React, { createContext, useContext, useState, useCallback, Component } from 'react';

/* سیستم تب سراسری برنامه */

const TabContext = createContext(null);
export const useTabs = () => useContext(TabContext);

const MAX_TABS = 5;

class TabErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('Tab crashed:', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{padding:'40px',textAlign:'center',fontFamily:"'Vazirmatn',Tahoma,sans-serif"}}>
          <div style={{fontSize:'40px',marginBottom:'12px'}}>⚠️</div>
          <div style={{fontSize:'15px',color:'#b71c1c',fontWeight:'700',marginBottom:'8px'}}>
            خطایی در این تب رخ داد
          </div>
          <div style={{fontSize:'12px',color:'#888',direction:'ltr',fontFamily:'monospace'}}>
            {String(this.state.error?.message || this.state.error)}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export function TabProvider({ children, globalOverlay }) {
  const [tabs,     setTabs]     = useState([]);   // [{id, title, icon, render}]
  const [activeId, setActiveId] = useState(null);
  const [replaceModal, setReplaceModal] = useState(null);

  // باز یا فعال کردن تب
  const openTab = useCallback((id, title, icon, renderFn) => {
    setTabs(prev => {
      const exists = prev.find(t => t.id === id);
      if (exists) {
        setActiveId(id);
        return prev;
      }
      const newTab = { id, title, icon, render: renderFn };
      if (prev.length >= MAX_TABS) {
        setReplaceModal({ pending: newTab });
        return prev;
      }
      setActiveId(id);
      return [...prev, newTab];
    });
  }, []);

  // بستن کامل یک تب
  const closeTab = useCallback((id, e) => {
    if (e) e.stopPropagation();
    setTabs(prev => {
      const idx = prev.findIndex(t => t.id === id);
      const next = prev.filter(t => t.id !== id);
      setActiveId(curActive => {
        if (curActive !== id) return curActive;
        if (next.length === 0) return null;
        const newIdx = Math.min(idx, next.length - 1);
        return next[newIdx].id;
      });
      return next;
    });
  }, []);

  const activateTab = useCallback((id) => setActiveId(id), []);

  const confirmReplace = useCallback((oldId) => {
    setTabs(prev => {
      const next = prev.filter(t => t.id !== oldId);
      const pending = replaceModal?.pending;
      if (!pending) return prev;
      setActiveId(pending.id);
      return [...next, pending];
    });
    setReplaceModal(null);
  }, [replaceModal]);

  const cancelReplace = useCallback(() => setReplaceModal(null), []);

  // مخفی کردن تب بدون بستن
  const goHome = useCallback(() => setActiveId(null), []);

  const hasActiveTab = activeId !== null;

  return (
    <TabContext.Provider value={{ tabs, activeId, hasActiveTab, openTab, closeTab, activateTab, goHome }}>

      {/* ── محتوای سرشاخه‌ها */}
      <div style={{display: hasActiveTab ? 'none' : 'block'}}>
        {children}
      </div>

      {/* محتوای تب‌های فعال */}
      {tabs.map(t => (
        <div key={t.id} style={{display: t.id === activeId ? 'block' : 'none'}}>
          <TabErrorBoundary>
            {t.render()}
          </TabErrorBoundary>
        </div>
      ))}

      {/* پاپ‌آپ انتخاب تب برای جایگزینی */}
      {replaceModal && (
        <div style={s.overlay} onClick={cancelReplace}>
          <div style={s.modal} onClick={e=>e.stopPropagation()}>
            <div style={s.modalHeader}>
              <div style={s.modalTitle}>⚠️ ظرفیت تب‌ها پر شده</div>
              <button onClick={cancelReplace} style={s.closeBtn}>✕</button>
            </div>
            <div style={s.modalBody}>
              <div style={{marginBottom:'14px',fontSize:'13px',color:'#555'}}>
                حداکثر {MAX_TABS} تب همزمان قابل باز شدن است. برای باز کردن
                «<b>{replaceModal.pending.title}</b>»، یکی از تب‌های زیر را انتخاب کنید تا بسته شود:
              </div>
              {tabs.map(t => (
                <button key={t.id} onClick={()=>confirmReplace(t.id)} style={s.replaceOption}>
                  <span style={{fontSize:'16px'}}>{t.icon}</span>
                  <span style={{flex:1,textAlign:'right'}}>{t.title}</span>
                  <span style={{fontSize:'11px',color:'#999'}}>بستن و جایگزینی</span>
                </button>
              ))}
            </div>
            <div style={s.modalFooter}>
              <button onClick={cancelReplace} style={s.btnCancel}>انصراف</button>
            </div>
          </div>
        </div>
      )}
      {/* ── لایه‌ی سراسری */}
      {globalOverlay}
    </TabContext.Provider>
  );
}

/* کامپوننت نوار تب */
export function TabBar() {
  const { tabs, activeId, activateTab, closeTab } = useTabs();
  if (tabs.length === 0) return null;
  return (
    <div style={s.tabBar}>
      <div style={s.tabBarInner}>
        {tabs.map(t => {
          const active = t.id === activeId;
          return (
            <div key={t.id}
              onClick={() => activateTab(t.id)}
              style={{...s.tab, ...(active ? s.tabActive : {})}}
              onMouseEnter={e=>{if(!active)e.currentTarget.style.background='rgba(255,255,255,0.08)';}}
              onMouseLeave={e=>{if(!active)e.currentTarget.style.background='transparent';}}>
              <span style={{fontSize:'14px'}}>{t.icon}</span>
              <span style={s.tabLabel}>{t.title}</span>
              <button onClick={e=>closeTab(t.id,e)} style={s.tabClose}
                onMouseEnter={e=>{e.currentTarget.style.background='rgba(255,255,255,0.25)';}}
                onMouseLeave={e=>{e.currentTarget.style.background='transparent';}}>
                ✕
              </button>
            </div>
          );
        })}
        <div style={s.tabCount}>{tabs.length}/{MAX_TABS}</div>
      </div>
    </div>
  );
}

const s = {
  tabBar:      {background:'#0a1628',borderBottom:'2px solid #C8A84B',
               position:'sticky',top:0,zIndex:90,overflowX:'auto'},
  tabBarInner: {display:'flex',alignItems:'center',gap:'2px',padding:'0 16px',minHeight:'40px'},
  tab:         {display:'flex',alignItems:'center',gap:'7px',padding:'8px 14px',
               cursor:'pointer',borderRadius:'8px 8px 0 0',whiteSpace:'nowrap',
               fontSize:'12px',color:'rgba(255,255,255,0.65)',transition:'background .15s',
               borderBottom:'3px solid transparent'},
  tabActive:   {background:'rgba(200,168,75,0.15)',color:'#C8A84B',
               borderBottom:'3px solid #C8A84B',fontWeight:'700'},
  tabLabel:    {fontFamily:"'Vazirmatn',Tahoma,sans-serif"},
  tabClose:    {background:'transparent',border:'none',color:'inherit',cursor:'pointer',
               width:'18px',height:'18px',borderRadius:'4px',fontSize:'11px',
               display:'flex',alignItems:'center',justifyContent:'center',
               marginRight:'-2px',transition:'background .15s'},
  tabCount:    {marginRight:'auto',fontSize:'11px',color:'rgba(255,255,255,0.4)',
               fontFamily:'monospace',paddingRight:'8px'},
  overlay:     {position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:2000,
               display:'flex',alignItems:'center',justifyContent:'center',padding:'16px'},
  modal:       {background:'#fff',borderRadius:'16px',width:'100%',maxWidth:'440px',
               boxShadow:'0 20px 60px rgba(0,0,0,0.4)',fontFamily:"'Vazirmatn',Tahoma,sans-serif"},
  modalHeader: {display:'flex',justifyContent:'space-between',alignItems:'center',
               padding:'16px 20px',background:'linear-gradient(135deg,#e65100,#f57c00)',
               borderRadius:'16px 16px 0 0'},
  modalTitle:  {fontSize:'14px',fontWeight:'700',color:'#fff'},
  closeBtn:    {background:'rgba(255,255,255,0.15)',border:'1px solid rgba(255,255,255,0.3)',
               color:'#fff',width:'28px',height:'28px',borderRadius:'50%',cursor:'pointer',
               fontSize:'12px',display:'flex',alignItems:'center',justifyContent:'center'},
  modalBody:   {padding:'18px 20px',direction:'rtl'},
  replaceOption:{display:'flex',alignItems:'center',gap:'8px',width:'100%',
               padding:'10px 14px',marginBottom:'6px',background:'#f8f9fa',
               border:'1.5px solid #e0e0e0',borderRadius:'8px',cursor:'pointer',
               fontFamily:'inherit',fontSize:'13px',color:'#333',textAlign:'right'},
  modalFooter: {display:'flex',justifyContent:'flex-end',padding:'10px 20px 16px'},
  btnCancel:   {padding:'8px 18px',background:'#e0e0e0',color:'#333',border:'none',
               borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px'},
};